import { Controller } from "@hotwired/stimulus"
import { GridSelection } from "../helpers/grid_selection"

// The Multibericht grid (report_center/multi_entries/_grid.html.erb): one form whose rows each
// become one report when "Speichern" posts them all. Nothing is saved before that.
//
// It edits like the list tables (grid-cells, the shared GridSelection): a click selects a cell
// and a second one — or Enter, F2, a double-click, or simply typing — edits it; Enter commits
// and moves down, Tab right, Escape puts the old value back, Entf clears. Unlike there, every
// cell holds its control all the time, so "editing" is only focus and a white cell: nothing is
// fetched, and nothing is saved until the form is.
//
// - Enter in the last row adds a row, so a column can be typed straight down.
// - "Zeile hinzufügen" clones the <template> row under the next free integer index — strong
//   params drop row keys that are not numbers — dated like the row above it.
// - "Zeile duplizieren" inserts a copy of the row below it; "Zeile entfernen" drops it.
// - A row's task picker follows its Baustelle; each site's tasks are fetched once.
// - Optional field columns can be hidden; the choice is kept per report type in this browser.
const INDEX = "__INDEX__"
// The controls that carry a row's values: every named field plus the visible half of a
// date field (Common::DateFieldComponent), which has no name of its own.
const VALUE_CONTROLS = "[name], [data-date-picker-target='display']"
// The control a cell is edited through. A date field's visible input comes before its hidden
// ISO companion, and a SlimSelect cell's is the original <select>.
const CELL_CONTROL = "input:not([type=hidden]), select"
// Where a cell's own pickers render: the date calendar (a popover) and SlimSelect's list (<body>).
const OUTSIDE_PICKERS = "[popover], .ss-content"

export default class extends GridSelection(Controller) {
  static targets = ["body", "template", "row", "columnToggle", "scroller"]
  static values = { tasksUrl: String, nextIndex: Number, storageKey: String }

  connect() {
    this.selected = null
    this.tabOrigin = null
    this.editingCell = null
    this.initialValue = null
    this.taskCache = new Map()
    // Only columns that still have a toggle: a field made required since it was hidden would
    // otherwise stay hidden with no way back, and fail every row.
    const toggleable = new Set(this.columnToggleTargets.map((toggle) => toggle.dataset.column))
    this.hiddenColumns = new Set(this.readHiddenColumns().filter((key) => toggleable.has(key)))
    this.applyHiddenColumns(this.element)
    // On DOCUMENT: SlimSelect's search box lives in <body>, so its keys never pass the form.
    this.onKeydownCapture = this.pickerKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydownCapture, true)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydownCapture, true)
  }

  get gridScroller() {
    return this.scrollerTarget
  }

  // A hidden column is not on screen, so the selection steps over it.
  cellsOf(row) {
    return super.cellsOf(row).filter((cell) => !cell.classList.contains("hidden"))
  }

  // --- pointer --------------------------------------------------------------------------------

  // The first click on a cell only selects it, as in Excel; the second reaches its control and
  // starts the edit. Held back from mousedown on, so the control neither takes focus nor opens
  // its list or calendar. A checkbox ticks at once, as in the list tables.
  pointerDown(event) {
    const cell = this.cellOf(event.target)
    if (!cell || cell === this.editingCell || cell.hasAttribute("data-selected")) return
    if (event.target.matches("input[type=checkbox]")) return

    event.preventDefault()
    this.heldClick = true
    this.tabOrigin = null
    this.stopEditing()
    this.select(cell)
  }

  pointerClick(event) {
    if (!this.heldClick) return

    this.heldClick = false
    event.preventDefault()
    event.stopPropagation()
  }

  doubleClick(event) {
    const cell = this.cellOf(event.target)
    if (cell && cell !== this.editingCell) this.startEditing(cell)
  }

  // --- keyboard -------------------------------------------------------------------------------

  keydown(event) {
    if (event.isComposing) return
    if (this.editingCell) {
      this.editingKeydown(event)
      return
    }

    // Only keys on the selected cell itself: Enter on "Speichern" or a row button stays theirs.
    const cell = event.target.matches?.("td[data-column]") ? event.target : null
    if (!cell || !cell.hasAttribute("data-selected")) return
    if (this.navigationKey(event, cell)) return

    if (event.key === "Enter" || event.key === "F2") {
      event.preventDefault()
      this.startEditing(cell)
    } else if (event.key === "Escape") {
      this.tabOrigin = null
      this.clearSelection()
    } else if (event.key === " " && this.controlOf(cell)?.type === "checkbox") {
      event.preventDefault()
      this.toggleCheckbox(this.controlOf(cell))
    } else if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault()
      this.writeValue(cell, "")
    } else if (this.printable(event)) {
      event.preventDefault()
      this.startEditing(cell, { typed: event.key })
    }
  }

  editingKeydown(event) {
    if (event.key === "Enter") {
      // A picker's Enter belongs to SlimSelect, which applies the option; `changed` then ends the edit.
      if (this.controlOf(this.editingCell)?.tagName === "SELECT") return

      // The date field reads its typed date on Enter first; the value is settled by now.
      event.preventDefault()
      this.finishEditing(this.enterMove(event.shiftKey))
    } else if (event.key === "Tab") {
      event.preventDefault()
      this.startTabRun()
      this.finishEditing(this.tabMove(event.shiftKey))
    } else if (event.key === "Escape") {
      // The first Escape in a date field closes its calendar and stops there.
      if (event.defaultPrevented) return
      event.preventDefault()
      this.writeValue(this.editingCell, this.initialValue)
      this.finishEditing()
    }
  }

  // Enter or Escape in an open picker means the cell is done. Queued, so SlimSelect applies or
  // closes first; with its list still open (Enter opened it) the edit goes on.
  pickerKeydown(event) {
    if (event.key !== "Enter" && event.key !== "Escape") return
    const cell = this.editingCell
    if (!cell || this.controlOf(cell)?.tagName !== "SELECT") return

    setTimeout(() => {
      if (this.editingCell !== cell || document.querySelector(".ss-content.ss-open")) return
      this.finishEditing()
    }, 0)
  }

  // --- editing --------------------------------------------------------------------------------

  // `typed` is the key that started a type-over: it replaces the value, as in Excel. Opened
  // without one (Enter, F2, double-click) the value stays and is selected.
  startEditing(cell, { typed = null } = {}) {
    const control = this.controlOf(cell)
    if (!control) return
    if (control.type === "checkbox") {
      this.toggleCheckbox(control)
      return
    }

    this.markEditing(cell)
    if (control.tagName === "SELECT") {
      const main = cell.querySelector(".ss-main")
      main?.focus()
      main?.click()
      return
    }

    control.focus()
    if (typed === null) {
      control.select?.()
    } else {
      control.value = typed
      control.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // Focus that reaches a cell's control by any other way (the second click, a picker taking it)
  // is an edit too. A checkbox is never "open": its cell stays selected and takes the keys.
  focusIn(event) {
    const cell = this.cellOf(event.target)
    if (!cell || event.target === cell || cell === this.editingCell) return
    if (event.target.matches("input[type=checkbox]")) {
      this.select(cell)
      return
    }

    this.stopEditing()
    this.markEditing(cell)
  }

  // Leaving the cell ends the edit and keeps what was typed. Moving into the cell's own calendar
  // or SlimSelect list is not leaving it.
  focusOut(event) {
    const cell = this.editingCell
    if (!cell) return
    const next = event.relatedTarget
    if (next && (cell.contains(next) || next.closest(OUTSIDE_PICKERS))) return

    setTimeout(() => {
      if (this.editingCell !== cell) return
      const active = document.activeElement
      if (cell.contains(active) || active?.closest(OUTSIDE_PICKERS)) return

      this.stopEditing()
    }, 0)
  }

  // A picked option is the whole edit of a picker cell. Queued: SlimSelect hands the focus back
  // to its own control after the pick, which would otherwise take the arrow keys from the grid.
  changed(event) {
    const cell = this.editingCell
    if (event.target.tagName !== "SELECT" || !cell?.contains(event.target)) return

    setTimeout(() => {
      if (this.editingCell === cell) this.finishEditing()
    }, 0)
  }

  markEditing(cell) {
    this.select(cell, { focus: false })
    this.editingCell = cell
    this.initialValue = this.controlOf(cell)?.value ?? ""
    cell.setAttribute("data-editing", "")
  }

  stopEditing() {
    this.editingCell?.removeAttribute("data-editing")
    this.editingCell = null
  }

  // Back to the selected cell, then on by `move`. Enter in the last row adds the next one first.
  finishEditing(move = null) {
    const cell = this.editingCell
    if (!cell) return

    this.stopEditing()
    this.select(cell)
    if (!move) return

    if (move[0] > 0 && cell.closest("tr") === this.rows.at(-1)) this.appendRow()
    this.moveSelection(...move)
  }

  paste(event) {
    if (this.editingCell || !event.target.matches?.("td[data-column][data-selected]")) return

    event.preventDefault()
    this.writeValue(event.target, event.clipboardData.getData("text/plain").trim())
  }

  // Delete and paste write without opening the cell. A picker is only ever cleared that way —
  // a pasted string is not one of its options — and a checkbox has no text.
  writeValue(cell, value) {
    const control = this.controlOf(cell)
    if (!control || control.type === "checkbox") return

    if (control.tagName === "SELECT") {
      if (value === "") this.selectController(control)?.clear()
      return
    }
    control.value = value
    control.dispatchEvent(new Event("input", { bubbles: true }))
    control.dispatchEvent(new Event("change", { bubbles: true }))
  }

  toggleCheckbox(checkbox) {
    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }

  cellOf(element) {
    const cell = element.closest?.("td[data-column]")
    return cell && this.bodyTarget.contains(cell) ? cell : null
  }

  controlOf(cell) {
    return cell.querySelector(CELL_CONTROL)
  }

  // The pinned date column casts its shadow once the table is scrolled sideways (application.css).
  scrolled() {
    this.scrollerTarget.toggleAttribute("data-scrolled-x", this.scrollerTarget.scrollLeft > 0)
  }

  // --- rows -----------------------------------------------------------------------------------

  addRow() {
    this.select(this.cellsOf(this.appendRow())[0])
  }

  duplicateRow(event) {
    const source = event.target.closest("tr")
    const row = this.buildRow()
    this.copyValues(source, row)
    this.insertAfter(row, source)
    this.select(this.cellsOf(row)[0])
  }

  removeRow(event) {
    const row = event.target.closest("tr")
    if (this.selected?.rowId === row.dataset.rowId) this.clearSelection()
    if (this.editingCell && row.contains(this.editingCell)) this.stopEditing()
    row.remove()
    if (this.rowTargets.length === 0) this.addRow()
  }

  appendRow() {
    const last = this.rowTargets.at(-1)
    const row = this.buildRow()
    if (last) this.copyDate(last, row)
    this.insertAfter(row, last)
    return row
  }

  buildRow() {
    const holder = document.createElement("tbody")
    holder.innerHTML = this.templateTarget.innerHTML.replaceAll(INDEX, String(this.nextIndexValue))
    this.nextIndexValue += 1
    return holder.querySelector("tr")
  }

  // Inserted after its values are set, so the select controllers connect with them in place.
  insertAfter(row, reference) {
    if (reference) {
      reference.after(row)
    } else {
      this.bodyTarget.append(row)
    }
    this.applyHiddenColumns(row)
  }

  copyDate(source, row) {
    const pairs = this.controlPairs(source, row).filter(([from]) =>
      from.closest("[data-column='date']"),
    )
    pairs.forEach(([from, to]) => (to.value = from.value))
  }

  // Both rows render from the same partial, so their controls pair up in document order. A task
  // picker takes the source's options first: they depend on the row's site.
  copyValues(source, row) {
    this.controlPairs(source, row).forEach(([from, to]) => {
      if (from.dataset.multiEntryGridRole === "task") to.innerHTML = from.innerHTML
      if (to.type === "checkbox") {
        to.checked = from.checked
      } else if (to.tagName === "SELECT") {
        ;[...to.options].forEach((option) => (option.selected = option.value === from.value))
      } else {
        to.value = from.value
      }
    })
  }

  controlPairs(source, row) {
    const from = [...source.querySelectorAll(VALUE_CONTROLS)]
    const to = [...row.querySelectorAll(VALUE_CONTROLS)]
    return from.map((control, index) => [control, to[index]]).filter(([, target]) => target)
  }

  // --- tasks ----------------------------------------------------------------------------------

  async siteChanged(event) {
    const row = event.target.closest("tr")
    const taskSelect = row?.querySelector("[data-multi-entry-grid-role='task']")
    if (!taskSelect) return

    const siteId = event.target.value
    const tasks = siteId ? await this.tasksFor(siteId) : []
    // The site may have changed again while this fetch ran; the later change fills the picker.
    if (event.target.value !== siteId) return

    taskSelect.replaceChildren(
      this.placeholderOption(),
      ...tasks.map((task) => new Option(task.text, task.value)),
    )
    this.selectController(taskSelect)?.clear()
  }

  async tasksFor(siteId) {
    if (!this.taskCache.has(siteId)) {
      const url = new URL(this.tasksUrlValue, window.location.origin)
      url.searchParams.set("site_id", siteId)
      this.taskCache.set(
        siteId,
        fetch(url, { headers: { Accept: "application/json" } })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return response.json()
          })
          .catch(() => {
            // Not kept: the next change of a row to this site asks again.
            this.taskCache.delete(siteId)
            return []
          }),
      )
    }
    return this.taskCache.get(siteId)
  }

  placeholderOption() {
    const option = new Option("", "")
    option.dataset.placeholder = "true"
    return option
  }

  selectController(element) {
    return this.application.getControllerForElementAndIdentifier(element, "select")
  }

  // --- column visibility ----------------------------------------------------------------------

  toggleColumn(event) {
    const key = event.target.dataset.column
    if (event.target.checked) {
      this.hiddenColumns.delete(key)
    } else {
      this.hiddenColumns.add(key)
    }
    this.writeHiddenColumns()
    this.applyHiddenColumns(this.element)
  }

  applyHiddenColumns(root) {
    root.querySelectorAll("[data-column]").forEach((element) => {
      if (element.dataset.multiEntryGridTarget === "columnToggle") {
        element.checked = !this.hiddenColumns.has(element.dataset.column)
      } else {
        element.classList.toggle("hidden", this.hiddenColumns.has(element.dataset.column))
      }
    })
  }

  readHiddenColumns() {
    try {
      return JSON.parse(window.localStorage.getItem(this.storageKeyValue) || "[]")
    } catch {
      return []
    }
  }

  writeHiddenColumns() {
    try {
      window.localStorage.setItem(this.storageKeyValue, JSON.stringify([...this.hiddenColumns]))
    } catch {
      // Storage blocked (private window): the columns simply reset on the next visit.
    }
  }
}
